import React from 'react';

import PropTypes from 'prop-types';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Grid} from '@material-ui/core';

import {FieldShape} from 'commons/shapes';
import {InferPropTypes} from 'commons/types/Types';
import {
    EfeResponse,
    LegalRepresentativeServiceRequest,
    ScreenFilterValues,
    ValidationsCURPResponse,
    View,
} from 'commons/models';
import {EfeButtonPrimary, EfeFormSection, EfeSupportMessage} from 'components';
import {LayoutServices, ValidationsService} from 'services';
import {useAlert, useSpinner} from 'hooks';
import {useSnackbar} from 'notistack';
import {
    Roles,
    Messages,
    HttpCodes,
    ViewChildId,
    PersonTypes,
    SnackbarVariants,
} from 'commons/constants';
import {functions} from 'commons/utils';
import {AxiosError} from 'axios';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';

InnerFormRepresentativePersonalInfo.propTypes = {
    viewId: PropTypes.number.isRequired,
    viewKey: PropTypes.string.isRequired,
    personType: PropTypes.string.isRequired,
    fieldHandler: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function InnerFormRepresentativePersonalInfo(
    props: InferPropTypes<
        typeof InnerFormRepresentativePersonalInfo.propTypes &
            FormikProps<LegalRepresentativeServiceRequest>
    >
) {
    const {enqueueSnackbar} = useSnackbar();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const {
        viewId,
        values,
        errors,
        fields,
        viewKey,
        isSubmitting,
        fieldHandler,
        validateForm,
        setFieldValue,
    } = props;

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        validateForm();
    }, [fields]);

    React.useEffect(() => {
        if (ViewChildId.LEGAL_REPRESENTATIVE_PERSONAL_INFO == viewKey && values.foreignCitizen) {
            (async function () {
                try {
                    setIsLoading(true);
                    const request: ScreenFilterValues = {
                        viewId: viewId,
                        personType: PersonTypes.FISICA,
                        foreign: values.foreignCitizen,
                        rol: Roles.LEGAL_REPRESENTATIVE,
                        viewKey: ViewChildId.LEGAL_REPRESENTATIVE_PERSONAL_INFO,
                    };
                    const response: EfeResponse<View> = await LayoutServices.getViewByFilters(
                        request
                    );
                    if (response.data && response.data.fields) {
                        setIsLoading(false);
                        fieldHandler(response.data);
                    } else {
                        enqueueSnackbar(Messages.LAYOUT_ERROR, {
                            variant: SnackbarVariants.ERROR,
                        });
                        fieldHandler(undefined);
                        setIsLoading(false);
                    }
                } catch (error) {
                    setIsLoading(false);
                    fieldHandler(undefined);
                    showAlertError(error, Messages.LAYOUT_ERROR);
                }
            })();
        } else {
            fieldHandler(undefined);
        }
    }, [values.foreignCitizen]);

    function setValues(data: ValidationsCURPResponse) {
        setFieldValue('curp', data.curp);
        setFieldValue('name', data.nombre);
        setFieldValue('lastName', data.apellidoPaterno);
        setFieldValue('secondLastName', data.apellidoMaterno);
        const date = functions.standardDateToObject(data.fechaNacimiento);
        if (date) {
            setFieldValue('birthDate', date);
        }
        setFieldValue('validationResult', true);
    }

    async function validateCurp(curp: string) {
        if (curp && curp.length >= 18) {
            setIsLoading(true);
            try {
                const response: EfeResponse<ValidationsCURPResponse> =
                    await ValidationsService.validateCurp({curp: curp, documento: 0});
                if (response && response.data && response.data.curp == curp) {
                    setValues(response.data);
                } else {
                    enqueueSnackbar(Messages.CURP_VALIDATION_ERROR, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
            } catch (error) {
                console.error(error);
                let message = Messages.CURP_VALIDATION_UNAVAILABLE;
                if (error) {
                    const err = error as AxiosError;
                    if (HttpCodes.BAD_REQUEST == err.code || HttpCodes.NOT_FOUND == err.code) {
                        message = Messages.CURP_VALIDATION_ERROR;
                    }
                }
                enqueueSnackbar(message, {
                    variant: SnackbarVariants.ERROR,
                });
                setIsLoading(false);
            }
        }
    }

    React.useEffect(() => {
        if (!values.foreignCitizen) {
            validateCurp(values.curp.toUpperCase());
        }
    }, [values.curp]);

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default InnerFormRepresentativePersonalInfo;
